import React, { useId, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Grid,
  ListItemText,
  MobileStepper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import DockerImageCollapseCard from '../Add/DockerImageCollapseCard';
import FooterWrapper from '@tenant/core/AppLayout/components/AppFooter/FooterWrapper';
import useNextTick from '@tenant/utility/hooks/useNextTick';
import { grey } from '@mui/material/colors';
import { deepClone } from '@mui/x-data-grid/utils/utils';
import AppGlossary from '@tenant/core/App/Common/AppGlossary';
import MarketplaceAppSelect from '@tenant/core/App/Select/MarketplaceAppSelect';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import { useTheme } from '@emotion/react';
import AppAccordion from '@tenant/core/App/Accordion/AppAccordion';
import { DataGrid } from '@mui/x-data-grid';

const AppPackageAddForm = ({
  onPrevious = () => {},
  submitForm = () => {},
  setForm = () => {},
  validateField = () => {},
  handleChangeIsValidAppContainer = () => {},
  values = {},
  errors = {},
  loading = false,
  isValidAppContainer = false,
  step = 0,
  steps = [],
  maxStep = 2,
  minStep = 0,
}) => {
  const nextTick = useNextTick();
  const uniqueId = useId();
  const theme = useTheme();
  const [timeSubmitted, setTimeSubmitted] = useState(0);

  const setFieldValue = async (field, value, isTrigger = true) => {
    setForm(field, value, false);
    if (isTrigger) {
      await nextTick();
      validateField(field);
    }
  };

  const columns = [
    {
      field: 'Name',
      headerName: 'Container App Name',
      width: 300,
      sortable: true,
      flex: 1,
    },
    {
      field: 'Image',
      headerName: 'Container Image',
      width: 300,
      sortable: true,
      flex: 1,
      renderCell: (params) => params.row.ImageNameItem?.Name,
    },
  ];

  return (
    <Box sx={{ position: 'relative' }}>
      <Box>
        {step === 0 && (
          <Box mt={3}>
            <Grid container spacing={5}>
              <Grid item xs={12} sm={6}>
                <Box component='p' color='text.primary' fontSize={14} mb={2}>
                  Name
                  <Box component='span' ml={1} sx={{ color: 'error.main' }}>
                    *
                  </Box>
                </Box>
                <TextField
                  fullWidth
                  value={values.Name ?? ''}
                  error={!!errors.Name}
                  helperText={errors.Name}
                  placeholder='E.g. Joblogic application'
                  onChange={(e) => setFieldValue('Name', e.target.value, true)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box
                  component='p'
                  display='flex'
                  color='text.primary'
                  fontSize={14}
                  mb={2}
                >
                  Marketplace App
                  <Box component='span' ml={1} sx={{ color: 'error.main' }}>
                    *
                  </Box>
                  <AppGlossary title='Ensure all hosting resources are linked to a corresponding Shell App in the Marketplace to maintain centralized management and visibility' />
                </Box>
                <MarketplaceAppSelect
                  value={values.AppModel ?? null}
                  queryKey={uniqueId}
                  error={!!errors.AppModel}
                  helperText={errors.AppModel}
                  sx={{ '& .Mui-disabled': { background: grey[50] } }}
                  onChange={(e, value) => {
                    setFieldValue('AppModel', value);
                    setFieldValue('AppComponents', []);
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={12}>
                <Box component='p' color='text.primary' fontSize={14} mb={2}>
                  Description{' '}
                  <Box component='span' ml={1} sx={{ color: 'error.main' }}>
                    *
                  </Box>
                </Box>
                <TextField
                  fullWidth
                  multiline
                  rows={5}
                  value={values.Description ?? ''}
                  error={!!errors.Description}
                  helperText={errors.Description}
                  placeholder='E.g. Describe your application'
                  onChange={(e) =>
                    setFieldValue('Description', e.target.value, true)
                  }
                  sx={{
                    '& .Mui-disabled': { background: grey[50] },
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        )}

        {step === 1 && (
          <Box>
            <Grid container spacing={5}>
              <Grid item xs={12} sm={12}>
                <DockerImageCollapseCard
                  isError={!isValidAppContainer}
                  appId={values.AppModel?.Id}
                  timeSubmitted={timeSubmitted}
                  initialValues={deepClone(
                    values.AppComponents?.length > 0
                      ? values.AppComponents
                      : [
                          {
                            id: 'new-item-id-default',
                            Name: '',
                          },
                        ],
                  )}
                  handleChange={(newValue, isValid) => {
                    setFieldValue('AppComponents', deepClone(newValue));
                    handleChangeIsValidAppContainer(
                      newValue?.length > 0 && isValid,
                    );
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        )}

        {step === 2 && (
          <>
            <AppAccordion title='Information'>
              <Grid container spacing={5}>
                <Grid item xs={12} sm={6}>
                  <ListItemText primary='Name' secondary={values.Name} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <ListItemText
                    primary='Marketplace App'
                    secondary={values.AppModel?.Name}
                  />
                </Grid>
                <Grid item xs={12} sm={12}>
                  <ListItemText
                    primary='Description'
                    secondary={values.Description}
                    secondaryTypographyProps={{
                      sx: {
                        whiteSpace: 'pre',
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </AppAccordion>
            <Box height='auto'>
              <Typography mb={5} mt={5} variant='h4'>
                {`Components (${values.AppComponents.length})`}
              </Typography>
              <DataGrid
                hideFooter
                autoHeight
                disableColumnSelector
                disableColumnMenu
                rows={values?.AppComponents ?? []}
                columns={columns}
                getRowId={(row) => row.Name}
              />
            </Box>
          </>
        )}
      </Box>

      <FooterWrapper
        className='footer'
        sx={{
          margin: '20px -20px -16px -20px',
          backgroundColor: '#f4f7fe',
          height: '50px',
        }}
      >
        <Stack
          direction='row'
          justifyContent='end'
          sx={{
            position: 'sticky',
            bottom: 0,
            height: '100%',
            p: 2,
            width: '100%',
          }}
        >
          <MobileStepper
            variant='progress'
            steps={steps.length}
            position='static'
            activeStep={step}
            nextButton={
              step < maxStep ? (
                <Button
                  onClick={() => {
                    if (step === 1 && !isValidAppContainer) {
                      setTimeSubmitted((s) => s + 1);
                      return;
                    }
                    submitForm();
                  }}
                  disabled={step === maxStep}
                >
                  Next
                  {theme.direction === 'rtl' ? (
                    <KeyboardArrowLeft />
                  ) : (
                    <KeyboardArrowRight />
                  )}
                </Button>
              ) : (
                <LoadingButton
                  loading={loading}
                  variant='contained'
                  onClick={submitForm}
                >
                  Create
                </LoadingButton>
              )
            }
            backButton={
              <Button onClick={onPrevious} disabled={step === minStep}>
                {theme.direction === 'rtl' ? (
                  <KeyboardArrowRight />
                ) : (
                  <KeyboardArrowLeft />
                )}
                Previous
              </Button>
            }
            sx={{ width: '100%' }}
          />
        </Stack>
      </FooterWrapper>
    </Box>
  );
};

export default React.memo(AppPackageAddForm);

AppPackageAddForm.propTypes = {
  onPrevious: PropTypes.func,
  submitForm: PropTypes.func,
  setForm: PropTypes.func,
  validateField: PropTypes.func,
  handleChangeIsValidAppContainer: PropTypes.func,
  values: PropTypes.object,
  errors: PropTypes.object,
  loading: PropTypes.bool,
  isValidAppContainer: PropTypes.bool,
  step: PropTypes.number,
  steps: PropTypes.array,
  maxStep: PropTypes.number,
  minStep: PropTypes.number,
};
